import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import logo from "@/assets/logo-optimized.webp";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const LandingNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const navLinks = [
    { label: t("nav.start"), path: "/" },
    { label: t("nav.howItWorks"), path: "/how-it-works" },
    { label: t("nav.features", "Features"), path: "/features" },
    { label: t("nav.pricing"), path: "/pricing" },
    { label: t("nav.applications"), path: "/applications" },
    { label: t("nav.blog", "Blog"), path: "/blog" },
    { label: t("nav.aboutUs"), path: "/about-us" },
    { label: t("nav.help"), path: "/help" },
  ];

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {navLinks.map((link) => (
        <Button
          key={link.path}
          variant="ghost"
          asChild
          className={mobile ? "w-full justify-start text-lg" : ""}
          onClick={mobile ? () => setIsOpen(false) : undefined}
        >
          <Link to={link.path}>{link.label}</Link>
        </Button>
      ))}
      <Button
        asChild
        className={mobile ? "w-full" : ""}
        onClick={mobile ? () => setIsOpen(false) : undefined}
      >
        <Link to="/auth">{t("nav.signIn")}</Link>
      </Button>
    </>
  );

  return (
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
      <div className="container mx-auto px-6 py-4">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logo} 
              alt="TestAssets" 
              className="h-8 w-8 object-contain" 
              fetchPriority="high"
              loading="eager"
              decoding="async"
              width={32}
              height={32}
            />
            <span className="text-xl font-semibold text-foreground">TestAssets</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <NavLinks />
            <LanguageSwitcher />
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("nav.openMenu")}>
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col gap-4 mt-8">
                  <NavLinks mobile />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default LandingNav;
